import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Share2, Link2, Download, RotateCcw, ExternalLink, Check } from "lucide-react";
import { posterDataUrl, downloadPoster } from "@/lib/poster";
import { sound } from "@/lib/audio";

const SHARE_TEXT =
    "The Mary Manzur Experience — Birthday Edition 2026. Certified fabulous. Universally appreciated. Officially exempt from all responsibilities today.";

export default function SharePoster({ onReplay, reduce }) {
    const [poster, setPoster] = useState(null);
    const [copied, setCopied] = useState(false);
    const [shared, setShared] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        let alive = true;
        const t = setTimeout(() => {
            posterDataUrl()
                .then((url) => { if (alive) setPoster(url); })
                .catch(() => { /* canvas unsupported */ });
        }, 300);
        return () => { alive = false; clearTimeout(t); };
    }, []);

    const pageUrl = () => window.location.origin + window.location.pathname;

    const copyLink = async () => {
        const url = pageUrl();
        try {
            await navigator.clipboard.writeText(url);
        } catch (e) {
            const ta = document.createElement("textarea");
            ta.value = url;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch (err) { /* clipboard unavailable */ }
            ta.remove();
        }
        sound.chime(880);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const share = async () => {
        const payload = { title: "The Mary Manzur Experience", text: SHARE_TEXT, url: pageUrl() };
        if (navigator.share) {
            try {
                await navigator.share(payload);
                sound.chime(1046.5);
                setShared(true);
                setTimeout(() => setShared(false), 2500);
                return;
            } catch (e) {
                if (e && e.name === "AbortError") return;
            }
        }
        await copyLink();
    };

    const save = async () => {
        const ok = await downloadPoster();
        if (ok) {
            sound.chime(783.99);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        }
    };

    return (
        <section data-testid="share-section" className="relative z-[1] px-6 py-24 sm:py-32">
            <div className="mx-auto max-w-4xl">
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <p className="eyebrow-label mb-4">The Encore — Take It With You</p>
                    <h2 className="font-display text-3xl font-semibold sm:text-5xl">
                        Your official <span className="gold-text italic">birthday poster</span>
                    </h2>
                    <p className="mx-auto mt-4 max-w-md text-sm font-light text-[#EDE7F8]/75 sm:text-base">
                        Frame it, share it, or deploy it as evidence that 2026 is officially your year.
                    </p>
                </motion.div>

                <div className="mt-14 grid items-center gap-10 lg:grid-cols-2">
                    <motion.div
                        className="relative mx-auto w-full max-w-sm"
                        initial={reduce ? { opacity: 0 } : { opacity: 0, rotateY: -18, y: 40 }}
                        whileInView={reduce ? { opacity: 1 } : { opacity: 1, rotateY: 0, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        style={{ perspective: 1000 }}
                    >
                        <div className="absolute -inset-4 rounded-3xl bg-[#F3D085]/10 blur-2xl" aria-hidden="true" />
                        {poster ? (
                            <img
                                data-testid="poster-preview"
                                src={poster}
                                alt="The Mary Manzur Experience 2026 birthday poster — gold celestial frame with cake and stars"
                                className="relative w-full rounded-xl border border-[#F3D085]/40 shadow-[0_40px_90px_-20px_rgba(0,0,0,0.8)]"
                            />
                        ) : (
                            <div className="relative flex aspect-[4/5] w-full items-center justify-center rounded-xl border border-[#F3D085]/30 bg-[#160E2A]">
                                <span className="eyebrow-label">Framing your poster…</span>
                            </div>
                        )}
                    </motion.div>

                    <motion.div
                        className="flex flex-col gap-3.5"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <button data-testid="share-poster-button" onClick={share} className="btn-gold w-full sm:w-auto">
                            {shared ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
                            {shared ? "Shared beautifully" : "Share my birthday experience"}
                        </button>
                        <button data-testid="copy-link-button" onClick={copyLink} className="btn-ghost w-full sm:w-auto">
                            {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
                            {copied ? "Link copied to clipboard" : "Copy my birthday link"}
                        </button>
                        <button data-testid="download-poster-button" onClick={save} className="btn-ghost w-full sm:w-auto">
                            {saved ? <Check className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                            {saved ? "Poster saved" : "Save my birthday poster"}
                        </button>
                        <button data-testid="replay-experience-button" onClick={onReplay} className="btn-ghost w-full sm:w-auto">
                            <RotateCcw className="h-4 w-4" />
                            Celebrate again
                        </button>
                        <a
                            data-testid="visit-mahana-link"
                            href="https://mahanasacredway.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ghost w-full sm:w-auto"
                        >
                            <ExternalLink className="h-4 w-4" />
                            Visit Mahana Sacred Way
                        </a>
                    </motion.div>
                </div>

                <motion.footer
                    data-testid="site-footer"
                    className="mt-24 border-t border-[#F3D085]/15 pt-10 text-center"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                >
                    <p className="font-display text-xl font-light italic text-[#EDE7F8]/85 sm:text-2xl">
                        Made with appreciation, a little developer humour, and a lot of good wishes.
                    </p>
                    <p className="mt-3 font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#F3D085]/70">
                        — Jerry | WebTechGuider
                    </p>
                    <p className="mt-6 font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#C4B5FD]/40">
                        The Mary Manzur Experience · Birthday Edition 2026
                    </p>
                </motion.footer>
            </div>
        </section>
    );
}
